import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';

const emptyForm = {
  id: '',
  name: '',
  location: '',
  startDate: '',
  endDate: '',
  time: '9h00 - 11h00',
  players: 4,

  // Logo / image affichée dans StageList
  picture: '',

  // Grande image affichée dans StageRegistration
  registrationPicture: '',

  price: 30,
  priceRange: 'P50 à P150',
  infoPrice: '',
  visible: true,
  archived: false,
};

const AdminStageCatalog = () => {
  const [form, setForm] = useState(emptyForm);
  const [stages, setStages] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const [registrationImageFile, setRegistrationImageFile] = useState(null);
  const [uploadingRegistrationImage, setUploadingRegistrationImage] = useState(false);

  const isBusy = loading || uploadingRegistrationImage;

  const loadStages = async () => {
    try {
      setLoadingList(true);

      const snapshot = await getDocs(collection(db, 'stage_registrations'));

      const data = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const id = Number(data.id ?? docSnap.id);

          let participantsCount = 0;

          try {
            const inscriptionsSnapshot = await getDocs(
              collection(db, 'stage_registrations', String(id), 'inscriptions')
            );

            participantsCount = inscriptionsSnapshot.size;
          } catch (error) {
            console.warn('Impossible de compter les inscriptions du stage', id, error);
          }

          return {
            ...data,
            _docId: docSnap.id,
            id,
            participantsCount,
          };
        })
      );

      data.sort((a, b) => {
        const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
        const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;

        if (dateA !== dateB) return dateA - dateB;

        return Number(a.id || 0) - Number(b.id || 0);
      });

      setStages(data);
    } catch (error) {
      console.error('Erreur chargement stage_registrations:', error);
      alert('Erreur lors du chargement des stages.');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadStages();
  }, []);

  const nextId = useMemo(() => {
    if (!stages.length) return 1;

    return Math.max(...stages.map((stage) => Number(stage.id || 0)), 0) + 1;
  }, [stages]);

  const filteredStages = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return stages;

    return stages.filter((stage) => (
      String(stage.id || '').includes(term) ||
      String(stage.name || '').toLowerCase().includes(term) ||
      String(stage.location || '').toLowerCase().includes(term) ||
      String(stage.startDate || '').toLowerCase().includes(term) ||
      String(stage.endDate || '').toLowerCase().includes(term)
    ));
  }, [search, stages]);

  const getStageDocId = (stage) => {
    return String(stage._docId || stage.id);
  };

  const scrollToForm = () => {
    setTimeout(() => {
      document
        .getElementById('admin-stage-form')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setRegistrationImageFile(null);
  };

  const setNextStage = () => {
    setForm((prev) => ({
      ...prev,
      id: nextId,
      name: `Stage Padel n°${nextId}`,
    }));

    setRegistrationImageFile(null);
    scrollToForm();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = () => {
    if (!form.id) return 'ID obligatoire.';
    if (!form.name.trim()) return 'Nom du stage obligatoire.';
    if (!form.location.trim()) return 'Lieu obligatoire.';
    if (!form.startDate) return 'Date de début obligatoire.';
    if (!form.endDate) return 'Date de fin obligatoire.';
    if (!form.time.trim()) return 'Horaire obligatoire.';
    if (!form.players) return 'Nombre de places obligatoire.';

    if (!form.picture.trim()) {
      return 'Logo / image carte obligatoire. Exemple : BDL.jfif.';
    }

    if (!form.price && Number(form.price) !== 0) return 'Prix obligatoire.';
    if (!form.priceRange.trim()) return 'Niveau / catégorie obligatoire.';

    if (new Date(form.endDate) < new Date(form.startDate)) {
      return 'La date de fin ne peut pas être avant la date de début.';
    }

    return null;
  };

  const uploadRegistrationPicture = async () => {
    if (!registrationImageFile) {
      return form.registrationPicture.trim();
    }

    if (!form.id) {
      alert("Mets d'abord un ID de stage avant d'uploader une image.");
      return null;
    }

    try {
      setUploadingRegistrationImage(true);

      const storage = getStorage(
        undefined,
        'gs://mendyx-adafe.firebasestorage.app'
      );

      const extension = registrationImageFile.name.split('.').pop() || 'jpg';

      const cleanName = registrationImageFile.name
        .replace(/\.[^/.]+$/, '')
        .replace(/[^a-zA-Z0-9-_]/g, '-')
        .toLowerCase();

      const fileName = `stage-registration-${form.id}-${Date.now()}-${cleanName}.${extension}`;

      const storageRef = ref(
        storage,
        `stages/${form.id}/registration/${fileName}`
      );

      await uploadBytes(storageRef, registrationImageFile);

      const downloadURL = await getDownloadURL(storageRef);

      setForm((prev) => ({
        ...prev,
        registrationPicture: downloadURL,
      }));

      setRegistrationImageFile(null);

      return downloadURL;
    } catch (error) {
      console.error('Erreur upload image inscription stage:', error);
      alert("Erreur lors de l'upload de l'image d'inscription.");
      return null;
    } finally {
      setUploadingRegistrationImage(false);
    }
  };

  const buildStagePayload = (registrationPictureUrl) => {
    const payload = {
      id: Number(form.id),
      name: form.name.trim(),
      location: form.location.trim(),
      startDate: form.startDate,
      endDate: form.endDate || form.startDate,
      time: form.time.trim(),
      players: Number(form.players),

      // Logo de la carte StageList
      picture: form.picture.trim(),

      // Image de la page d'inscription StageRegistration
      registrationPicture: registrationPictureUrl || '',

      price: Number(form.price),
      priceRange: form.priceRange.trim(),
      visible: Boolean(form.visible),
      archived: Boolean(form.archived),
    };

    if (form.infoPrice.trim()) {
      payload.infoPrice = form.infoPrice.trim();
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMessage = validateForm();

    if (errorMessage) {
      alert(errorMessage);
      return;
    }

    try {
      setLoading(true);

      const registrationPictureUrl = await uploadRegistrationPicture();

      if (registrationPictureUrl === null) {
        return;
      }

      const payload = buildStagePayload(registrationPictureUrl);
      const documentId = String(payload.id);

      await setDoc(
        doc(db, 'stage_registrations', documentId),
        payload,
        { merge: true }
      );

      alert(`✅ Stage ${payload.id} enregistré dans stage_registrations.`);

      setForm(emptyForm);
      setRegistrationImageFile(null);
      await loadStages();
    } catch (error) {
      console.error('Erreur enregistrement stage:', error);
      alert("Erreur lors de l'enregistrement du stage.");
    } finally {
      setLoading(false);
    }
  };

  const editStage = (stage) => {
    setForm({
      id: stage.id ?? '',
      name: stage.name ?? '',
      location: stage.location ?? '',
      startDate: stage.startDate ?? '',
      endDate: stage.endDate ?? stage.startDate ?? '',
      time: stage.time ?? '9h00 - 11h00',
      players: stage.players ?? 4,
      picture: stage.picture ?? '',
      registrationPicture: stage.registrationPicture ?? '',
      price: stage.price ?? 30,
      priceRange: stage.priceRange ?? 'P50 à P150',
      infoPrice: stage.infoPrice ?? '',
      visible: stage.visible ?? true,
      archived: stage.archived ?? false,
    });

    setRegistrationImageFile(null);
    scrollToForm();
  };

  const duplicateStage = (stage) => {
    setForm({
      id: nextId,
      name: `Stage Padel n°${nextId}`,
      location: stage.location ?? '',
      startDate: stage.startDate ?? '',
      endDate: stage.endDate ?? stage.startDate ?? '',
      time: stage.time ?? '9h00 - 11h00',
      players: stage.players ?? 4,
      picture: stage.picture ?? '',
      registrationPicture: stage.registrationPicture ?? '',
      price: stage.price ?? 30,
      priceRange: stage.priceRange ?? 'P50 à P150',
      infoPrice: stage.infoPrice ?? '',
      visible: true,
      archived: false,
    });

    setRegistrationImageFile(null);
    scrollToForm();
  };

  const toggleVisibility = async (stage) => {
    try {
      setLoading(true);

      await setDoc(
        doc(db, 'stage_registrations', getStageDocId(stage)),
        {
          visible: !Boolean(stage.visible),
        },
        { merge: true }
      );

      await loadStages();
    } catch (error) {
      console.error('Erreur visibilité stage:', error);
      alert('Erreur lors du changement de visibilité.');
    } finally {
      setLoading(false);
    }
  };

  const archiveStage = async (stage) => {
    const confirmArchive = window.confirm(
      `Archiver le stage ${stage.id} ? Il ne sera plus visible sur le site.`
    );

    if (!confirmArchive) return;

    try {
      setLoading(true);

      await setDoc(
        doc(db, 'stage_registrations', getStageDocId(stage)),
        {
          archived: true,
          visible: false,
        },
        { merge: true }
      );

      await loadStages();
    } catch (error) {
      console.error('Erreur archivage stage:', error);
      alert("Erreur lors de l'archivage.");
    } finally {
      setLoading(false);
    }
  };

  const deleteStage = async (stage) => {
    const confirmDelete = window.confirm(
      `Supprimer définitivement la fiche du stage ${stage.id} ? Attention : les inscriptions en sous-collection ne sont pas automatiquement supprimées.`
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);

      await deleteDoc(doc(db, 'stage_registrations', getStageDocId(stage)));

      await loadStages();
    } catch (error) {
      console.error('Erreur suppression stage:', error);
      alert('Erreur lors de la suppression du stage.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#4c2f8e]">
          Admin - Gestion des stages
        </h1>

        <p className="text-gray-600 mt-2">
          Ajoute, modifie, duplique ou archive les stages dans stage_registrations.
        </p>

        <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
          Cette collection sert à la fois de catalogue des stages et de base pour les inscriptions.
          La sous-collection inscriptions reste attachée au stage.
        </div>
      </div>

      <form
        id="admin-stage-form"
        onSubmit={handleSubmit}
        className="bg-white shadow-lg rounded-2xl p-4 md:p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <div className="md:col-span-2 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            Ajouter / Modifier un stage
          </h2>

          <div className="flex flex-col md:flex-row gap-2">
            <button
              type="button"
              onClick={setNextStage}
              className="px-4 py-2 rounded-full bg-gray-900 text-white text-sm"
            >
              Pré-remplir prochain ID : {nextId}
            </button>

            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 rounded-full bg-gray-200 text-gray-800 text-sm"
            >
              Vider le formulaire
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">ID</label>
          <input
            name="id"
            type="number"
            value={form.id}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="7"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Nom</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="Stage Padel n°7"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold mb-1">Lieu</label>
          <input
            name="location"
            value={form.location}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="Bois du Loup"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Date début</label>
          <input
            name="startDate"
            type="date"
            value={form.startDate}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Date fin</label>
          <input
            name="endDate"
            type="date"
            value={form.endDate}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Horaire</label>
          <input
            name="time"
            value={form.time}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="9h00 - 11h00"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Places</label>
          <input
            name="players"
            type="number"
            value={form.players}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="4"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Prix</label>
          <input
            name="price"
            type="number"
            value={form.price}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="30"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Niveau / catégorie</label>
          <input
            name="priceRange"
            value={form.priceRange}
            onChange={handleChange}
            className="w-full border rounded-lg p-2"
            placeholder="P50 à P150"
            required
          />
        </div>

        <div className="md:col-span-2 border rounded-xl p-4 bg-gray-50">
          <label className="block text-sm font-semibold mb-1">
            Logo / image carte du stage
          </label>

          <input
            name="picture"
            value={form.picture}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 bg-white"
            placeholder="BDL.jfif"
            required
          />

          <p className="text-xs text-gray-500 mt-1">
            Sert uniquement à la carte dans la liste des stages. Exemple : BDL.jfif, padelmettet.jfif, padelone.jfif.
          </p>

          {form.picture && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">Aperçu logo / carte :</p>

              <img
                src={form.picture}
                alt="Aperçu logo club"
                className="w-32 h-32 object-contain rounded-lg border bg-white"
              />
            </div>
          )}
        </div>

        <div className="md:col-span-2 border rounded-xl p-4 bg-purple-50">
          <label className="block text-sm font-semibold mb-1">
            Image du formulaire d’inscription
          </label>

          <input
            type="file"
            accept="image/*"
            onChange={(e) => setRegistrationImageFile(e.target.files?.[0] || null)}
            className="w-full border rounded-lg p-2 bg-white"
          />

          <p className="text-xs text-gray-500 mt-1">
            Cette image sera affichée à gauche du formulaire d’inscription du stage.
          </p>

          {registrationImageFile && (
            <div className="mt-2 text-sm text-gray-700">
              Nouvelle image sélectionnée : <strong>{registrationImageFile.name}</strong>
            </div>
          )}

          {form.registrationPicture && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-1">
                Image actuelle du formulaire :
              </p>

              <img
                src={form.registrationPicture}
                alt="Aperçu inscription stage"
                className="w-48 h-48 object-contain rounded-lg border bg-white"
              />

              <label className="block text-xs font-semibold text-gray-500 mt-3 mb-1">
                URL enregistrée
              </label>

              <input
                name="registrationPicture"
                value={form.registrationPicture}
                onChange={handleChange}
                className="w-full border rounded-lg p-2 text-xs bg-white"
                placeholder="URL Firebase Storage ou /stage5.jpg"
              />
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold mb-1">
            Informations paiement
          </label>
          <textarea
            name="infoPrice"
            value={form.infoPrice}
            onChange={handleChange}
            className="w-full border rounded-lg p-2 min-h-[90px]"
            placeholder="30€ d’acompte à payer en ligne. Solde à payer le jour du stage..."
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            name="visible"
            type="checkbox"
            checked={form.visible}
            onChange={handleChange}
          />
          <label>Visible sur le site</label>
        </div>

        <div className="flex items-center gap-2">
          <input
            name="archived"
            type="checkbox"
            checked={form.archived}
            onChange={handleChange}
          />
          <label>Archivé</label>
        </div>

        <button
          type="submit"
          disabled={isBusy}
          className="md:col-span-2 bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white rounded-full py-3 font-semibold shadow-lg disabled:opacity-60"
        >
          {isBusy ? 'Enregistrement...' : 'Enregistrer le stage'}
        </button>
      </form>

      <div className="bg-white shadow-lg rounded-2xl p-4 md:p-6">
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            Stages dans stage_registrations
          </h2>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded-lg p-2 w-full md:w-80"
            placeholder="Recherche ID, lieu, date..."
          />
        </div>

        {loadingList ? (
          <p className="text-gray-500">Chargement...</p>
        ) : filteredStages.length === 0 ? (
          <p className="text-gray-500">
            Aucun stage trouvé dans stage_registrations.
          </p>
        ) : (
          <div className="space-y-3">
            {filteredStages.map((stage) => (
              <div
                key={getStageDocId(stage)}
                className={`border rounded-xl p-4 ${
                  stage.archived ? 'bg-gray-100 opacity-70' : 'bg-white'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex gap-4">
                    {stage.picture && (
                      <img
                        src={stage.picture}
                        alt={stage.name}
                        className="hidden sm:block w-20 h-20 object-contain rounded-lg border bg-gray-50"
                      />
                    )}

                    <div>
                      <div className="font-bold text-lg text-[#4c2f8e]">
                        #{stage.id} - {stage.name}
                      </div>

                      <div className="text-sm text-gray-600 mt-1">
                        {stage.startDate} | {stage.time}
                      </div>

                      <div className="text-sm text-gray-600">
                        {stage.location}
                      </div>

                      <div className="text-sm mt-1">
                        {stage.participantsCount || 0} / {stage.players} inscrits | {stage.price}€ | {stage.priceRange}
                      </div>

                      <div className="text-xs text-gray-500 mt-1 break-all">
                        Logo carte : {stage.picture || 'Aucun'}
                      </div>

                      <div className="text-xs text-gray-500 mt-1 break-all">
                        Image formulaire : {stage.registrationPicture || 'Aucune'}
                      </div>

                      <div className="text-xs text-gray-500 mt-1">
                        Visible : {stage.visible ? 'oui' : 'non'} | Archivé : {stage.archived ? 'oui' : 'non'}
                      </div>

                      {stage.infoPrice && (
                        <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-lg text-xs text-gray-700">
                          {stage.infoPrice}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => editStage(stage)}
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-60"
                    >
                      Modifier
                    </button>

                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => duplicateStage(stage)}
                      className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm disabled:opacity-60"
                    >
                      Dupliquer
                    </button>

                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => toggleVisibility(stage)}
                      className="px-3 py-2 rounded-lg bg-yellow-500 text-white text-sm disabled:opacity-60"
                    >
                      {stage.visible ? 'Masquer' : 'Afficher'}
                    </button>

                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => archiveStage(stage)}
                      className="px-3 py-2 rounded-lg bg-gray-700 text-white text-sm disabled:opacity-60"
                    >
                      Archiver
                    </button>

                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => deleteStage(stage)}
                      className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-60"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-800 text-sm">
        Conseil : utilise plutôt “Archiver” que “Supprimer” si un stage a déjà des inscrits.
      </div>
    </div>
  );
};

export default AdminStageCatalog;